// Phase 1.9.18 — Invitation-level shared audio.
//
// One `<audio>` element per invitation, shared by:
//   • the cinematic opener tap-gate (which calls play() on the guest tap so
//     browser autoplay policy is satisfied by the gesture)
//   • the compact vinyl widget on the canvas (which reads playing state to
//     spin the disc and binds tap-to-pause/resume to the disc itself)
//
// Backward-compatibility (per Phase 1.9.18 product spec):
//   The resolver does NOT mutate or migrate saved data. It reads from
//   whichever canonical source is already configured today:
//     1. rsvpPage.background_music_url   (v1 legacy invitation-level field)
//     2. first canvas vinyl_player element with a non-empty musicUrl
//        (deterministic canvas order = saved array order)
//   If more than one vinyl element has a different musicUrl, the first is
//   used and a console.warn is emitted so designers can clean up later.
//
// A standalone CompactVinylPlayer (e.g. used in Studio without a provider)
// keeps working — see useInvitationAudio() which returns null when no
// provider is in the tree.

import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

const InvitationAudioCtx = createContext(null);

const API = process.env.REACT_APP_BACKEND_URL;

function abs(url) {
  if (!url) return '';
  if (url.startsWith('/')) return `${API}${url}`;
  return url;
}

/**
 * Resolve the invitation-level music source without mutating saved data.
 *
 * @param {Object} args
 * @param {Object} [args.rsvpPage]        Public invitation document.
 * @param {Array}  [args.canvasElements]  Optional v2 canvas elements array.
 * @returns {string} resolved absolute URL or ''
 */
export function resolveInvitationMusicUrl({ rsvpPage, canvasElements } = {}) {
  // 1) Canonical v1 field.
  const legacy = rsvpPage?.background_music_url;
  if (legacy) return abs(legacy);

  // 2) First vinyl/music canvas element in saved order.
  if (Array.isArray(canvasElements)) {
    let firstUrl = '';
    const seen = new Set();
    for (const el of canvasElements) {
      if (!el || el.type !== 'vinyl_player') continue;
      const u = el.musicUrl;
      if (!u) continue;
      if (!firstUrl) firstUrl = u;
      seen.add(u);
    }
    if (seen.size > 1) {
      // Don't guess silently — flag the conflict for later cleanup.
      // eslint-disable-next-line no-console
      console.warn('[invitation-audio] multiple distinct vinyl musicUrl values; using first canvas-order entry.', Array.from(seen));
    }
    return abs(firstUrl);
  }

  return '';
}

export function InvitationAudioProvider({ musicUrl = '', children }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [errored, setErrored] = useState(false);

  // Wire the audio element's native events to React state. Doing this in
  // a single mount-time effect keeps state in sync with the DOM truth and
  // avoids set-state-in-effect lint warnings.
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return undefined;
    const onPlay  = () => { setPlaying(true);  setErrored(false); };
    const onPause = () => setPlaying(false);
    const onEnded = () => setPlaying(false);
    const onError = () => { setErrored(true); setPlaying(false); };
    a.addEventListener('play',  onPlay);
    a.addEventListener('pause', onPause);
    a.addEventListener('ended', onEnded);
    a.addEventListener('error', onError);
    return () => {
      a.removeEventListener('play',  onPlay);
      a.removeEventListener('pause', onPause);
      a.removeEventListener('ended', onEnded);
      a.removeEventListener('error', onError);
      try { a.pause(); } catch (_) { /* noop */ }
    };
  }, []);

  const value = useMemo(() => {
    const play = async () => {
      const a = audioRef.current;
      if (!a || !musicUrl) return false;
      try {
        await a.play();
        return true;
      } catch (_) {
        // Autoplay blocked, etc. — caller decides what to do.
        return false;
      }
    };
    const pause = () => {
      const a = audioRef.current;
      if (a) { try { a.pause(); } catch (_) { /* noop */ } }
    };
    const toggle = () => {
      const a = audioRef.current;
      if (!a) return Promise.resolve(false);
      if (a.paused) return play();
      pause();
      return Promise.resolve(true);
    };
    return {
      audioRef,
      source: musicUrl,
      hasSource: !!musicUrl,
      playing,
      errored,
      play,
      pause,
      toggle,
    };
  }, [musicUrl, playing, errored]);

  return (
    <InvitationAudioCtx.Provider value={value}>
      {/* The <audio> element lives at the provider root so it is mounted
          exactly once and survives across opener replays / canvas mounts. */}
      {musicUrl ? (
        <audio
          ref={audioRef}
          src={musicUrl}
          preload="metadata"
          // No `loop` here — guests can re-tap the vinyl to restart on end.
          data-testid="invitation-audio"
        />
      ) : null}
      {children}
    </InvitationAudioCtx.Provider>
  );
}

/** Returns the current invitation audio context, or null if there's no provider. */
export function useInvitationAudio() {
  return useContext(InvitationAudioCtx);
}
